import { type ReactNode, useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useRouteLoaderData, useLocation } from 'react-router';

import { LocaleContext } from '@/i18n/LocaleContext';
import { createPublishedI18nSync } from '@/i18n/i18n';
import { LOCALE_STORAGE_KEY } from '@/i18n/manifest';
import { parseLocalePrefixedPath, resolvePublishedLocale } from '@/i18n/routing';
import type { PublishedLocaleManifest } from '@/i18n/types';

interface LocaleProviderProps {
    children: ReactNode;
}

interface RootLoaderData {
    manifest: PublishedLocaleManifest;
    locale: string;
    bundle: Record<string, Record<string, unknown>>;
}

function readSavedLocalePreference(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.localStorage.getItem(LOCALE_STORAGE_KEY);
}

/**
 * Phase 6 — synchronous LocaleProvider.
 *
 * Consumes the root loader's `{ manifest, locale, bundle }` payload via
 * `useRouteLoaderData("root")`, so i18next can be initialised on the very
 * first render. No `useState`/`useEffect` round-trip, no `<RouteLoading />`
 * fallback — prerendered HTML hydrates cleanly without a mismatch.
 *
 * The build-time HTML payload already contains the resolved manifest +
 * bundle, and React Router v7 serves the cached `.data` file on client
 * navigation under `ssr:false`, so the loader runs once per build and the
 * provider has full data on hydration.
 */
export function LocaleProvider({ children }: LocaleProviderProps) {
    const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
    const location = useLocation();

    if (!rootData) {
        throw new Error(
            'LocaleProvider: root loader data missing. `app/root.tsx` must export `loader`.',
        );
    }

    const { manifest, bundle } = rootData;

    const urlLocale = parseLocalePrefixedPath(
        location.pathname,
        manifest.locales.map(item => item.code),
    ).locale;

    const locale = resolvePublishedLocale({
        manifest,
        urlLocale,
        savedLocale: readSavedLocalePreference(),
        host: typeof window === 'undefined' ? '' : window.location.host,
    });

    const i18n = useMemo(
        () => createPublishedI18nSync(manifest, locale, bundle),
        [manifest, locale, bundle],
    );

    // Sync <html lang> + remembered locale preference on the client. Skipped on
    // SSR/prerender — the root <html lang="en"> attribute in `root.tsx` already
    // matches the default locale, so first-render output is stable.
    if (typeof document !== 'undefined') {
        document.documentElement.lang = locale;
    }
    if (typeof window !== 'undefined') {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }

    return (
        <LocaleContext.Provider value={{ manifest, locale }}>
            <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
        </LocaleContext.Provider>
    );
}
